import React, { useState, useEffect } from 'react';
import { useScrollRestore } from '../hooks/useScrollRestore';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  UserPlus,
  UserMinus,
  Image as ImageIcon,
  SignOut,
  Trash,
  Camera,
  X,
  PencilSimple,
  Check,
  ShieldCheck
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const GroupDashboard = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [group, setGroup] = useState(() => readInstantCache('group_dash', groupId) || null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(() => !readInstantCache('group_dash', groupId));
  const [activeTab, setActiveTab] = useState('members'); // members, media, settings
  const bodyScrollRef = useScrollRestore('group_dash_scroll', [group?.members?.length, media.length, activeTab]);
  const [uploadingPic, setUploadingPic] = useState(false);
  
  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    if (!user || !groupId) {
      navigate('/groups');
      return;
    }

    fetchGroupDetails();
    fetchMedia();
  }, [groupId, user, navigate]);

  const fetchGroupDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}`);
      setGroup(response.data);
      writeInstantCache('group_dash', groupId, response.data);
    } catch (error) {
      console.error('Failed to fetch group:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/media`);
      setMedia(response.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    }
  };

  const saveGroupName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) { toast.error('Name cannot be empty'); return; }
    setSavingName(true);
    try {
      await axios.put(`${API}/api/groups/${groupId}/name`, null, {
        params: { name: trimmed, actor_id: user.id },
      });
      setGroup((prev) => ({ ...prev, name: trimmed }));
      setEditingName(false);
      toast.success('Group name updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rename group');
    } finally {
      setSavingName(false);
    }
  };

  const promoteMember = async (memberId, makeAdmin) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/promote-admin`, null, {
        params: { member_id: memberId, actor_id: user.id, make_admin: makeAdmin },
      });
      setGroup((prev) => ({
        ...prev,
        members: prev.members.map((m) => (m.user_id === memberId ? { ...m, is_admin: makeAdmin } : m)),
      }));
      toast.success(makeAdmin ? 'Member promoted to admin' : 'Admin removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Remove this member from the group?')) return;

    try {
      await axios.post(`${API}/api/groups/${groupId}/remove-member?user_id=${memberId}&removed_by=${user.id}`);

      toast.success('Member removed successfully');
      fetchGroupDetails();
    } catch (error) {
      console.error('Failed to remove member:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to remove member';
      toast.error(errorMsg);
    }
  };

  // Image preview handlers
  const handleImageClick = (imageUrl) => {
    console.log('Image clicked:', imageUrl);
    let fullUrl;
    
    if (imageUrl.startsWith('http')) {
      fullUrl = imageUrl; // Already complete URL
    } else if (imageUrl.startsWith('/api/')) {
      fullUrl = `${API}${imageUrl}`; // /api/media/... → https://.../api/media/...
    } else if (imageUrl.startsWith('/uploads/')) {
      fullUrl = `${API}/api${imageUrl}`; // /uploads/... → https://.../api/uploads/...
    } else {
      fullUrl = `${API}/api${imageUrl}`; // Fallback
    }
    
    console.log('Preview URL:', fullUrl);
    setPreviewImageUrl(fullUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  };

  const leaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;

    try {
      await axios.post(`${API}/api/groups/${groupId}/remove-member?user_id=${user.id}&removed_by=${user.id}`);

      toast.success('You have left the group');
      navigate('/groups');
    } catch (error) {
      console.error('Failed to leave group:', error);
      toast.error('Failed to leave group');
    }
  };

  const isAdmin = group?.members?.find(m => m.user_id === user?.id)?.is_admin;
  const isCreator = group?.created_by === user?.id;

  const deleteGroup = async () => {
    if (!window.confirm('Are you sure you want to delete this group? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/api/groups/${groupId}?user_id=${user.id}`);
      toast.success('Group deleted successfully');
      navigate('/groups');
    } catch (error) {
      console.error('Failed to delete group:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete group';
      toast.error(errorMsg);
    }
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, GIF, or WebP');
      return;
    }

    try {
      setUploadingPic(true);
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${API}/api/groups/${groupId}/upload-profile`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Profile picture updated!');
      fetchGroupDetails(); // Refresh group data
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingPic(false);
    }
  };

  const deleteProfilePic = async () => {
    if (!window.confirm('Remove group profile picture?')) return;

    try {
      await axios.delete(`${API}/api/groups/${groupId}/profile`);
      toast.success('Profile picture removed');
      fetchGroupDetails();
    } catch (error) {
      console.error('Failed to delete profile picture:', error);
      toast.error('Failed to remove profile picture');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <>
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 z-10 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button 
            onClick={() => navigate(`/groups/${groupId}`)}
            className="hover:bg-green-700 p-2 rounded-full"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold">Group Info</h1>
        </div>
      </div>

      {/* PWA-safe scroll region — body/#root are overflow-hidden in
          standalone PWA, so this inner container owns the scroll. Fixes the
          Members tab (and every tab) not scrolling to show all rows. */}
      <div ref={bodyScrollRef} className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-4">
        {/* Group Icon & Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <div className="flex flex-col items-center">
            {/* Profile Picture with Upload */}
            <div className="relative mb-4">
              {group?.profile_pic ? (
                <img
                  src={`${API}/uploads/group_profiles/${group.profile_pic}`}
                  alt="Group"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                  <Users size={48} className="text-green-600" weight="bold" />
                </div>
              )}
              
              {isAdmin && (
                <div className="absolute bottom-0 right-0 flex gap-1">
                  <label className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-full cursor-pointer">
                    <Camera size={16} weight="bold" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePicUpload}
                      className="hidden"
                      disabled={uploadingPic}
                    />
                  </label>
                  
                  {group?.profile_pic && (
                    <button
                      onClick={deleteProfilePic}
                      className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full"
                    >
                      <X size={16} weight="bold" />
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {editingName ? (
              <div className="flex items-center gap-2 mb-2 w-full max-w-xs">
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveGroupName(); if (e.key === 'Escape') setEditingName(false); }}
                  maxLength={100}
                  className="flex-1 text-xl font-bold text-center border-b-2 border-green-500 focus:outline-none"
                  data-testid="group-name-input"
                />
                <button onClick={saveGroupName} disabled={savingName} className="text-green-600 p-1" data-testid="group-name-save">
                  <Check size={20} weight="bold" />
                </button>
                <button onClick={() => setEditingName(false)} className="text-gray-400 p-1">
                  <X size={20} weight="bold" />
                </button>
              </div>
            ) : (
              <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                {group?.name}
                {isAdmin && (
                  <button
                    onClick={() => { setNameDraft(group?.name || ''); setEditingName(true); }}
                    className="text-gray-400 hover:text-green-600"
                    title="Change group name"
                    data-testid="group-name-edit-btn"
                  >
                    <PencilSimple size={18} />
                  </button>
                )}
              </h2>
            )}

            {group?.description && (
              <p className="text-gray-600 text-center mb-4">
                {group.description}
              </p>
            )}

            <div className="text-sm text-gray-500">
              Created by {group?.members?.find(m => m.user_id === group.created_by)?.username || 'Unknown'}
              {' • '}
              {new Date(group?.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
            </div>

            <div className="mt-2 text-sm">
              <span className={`px-3 py-1 rounded-full ${
                group?.type === 'client_group' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
              }`}>
                {group?.type === 'client_group' ? 'Client Group' : 'Staff Group'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-4">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('members')}
              className={`flex-1 py-3 text-center font-medium ${
                activeTab === 'members'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-600'
              }`}
            >
              Members ({group?.members?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`flex-1 py-3 text-center font-medium ${
                activeTab === 'media'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-600'
              }`}
            >
              Media ({media.length})
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 py-3 text-center font-medium ${
                activeTab === 'settings'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-600'
              }`}
            >
              Settings
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {/* Members Tab */}
            {activeTab === 'members' && (
              <div>
                {isAdmin && (
                  <button
                    onClick={() => navigate(`/groups/${groupId}/add-members`)}
                    className="w-full mb-4 bg-green-600 text-white py-2 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-green-700"
                  >
                    <UserPlus size={20} weight="bold" />
                    Add Members
                  </button>
                )}

                {/* Latest-fix Point 8 — members list scrolls with the page
                    (no fixed-pixel maxHeight formula that broke on some
                    PWA/device sizes and clipped the last members). */}
                <div className="space-y-2">
                  {group?.members?.map((member, idx) => {
                    // Safely extract member data
                    const memberName = typeof member.username === 'string' 
                      ? member.username 
                      : (typeof member.username === 'object' && member.username?.username) 
                      ? member.username.username 
                      : 'Unknown';
                    
                    const memberRole = typeof member.role === 'string' 
                      ? member.role.replace('_', ' ') 
                      : 'Member';

                    return (
                      <div
                        key={member.user_id || idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium">{memberName}</div>
                          <div className="text-sm text-gray-500">
                            {memberRole}
                            {member.is_admin && (
                              <span className="ml-2 text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
                                Admin
                              </span>
                            )}
                          </div>
                        </div>

                        {isAdmin && member.user_id !== user?.id && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => promoteMember(String(member.user_id || ''), !member.is_admin)}
                              className="text-purple-600 hover:text-purple-700 p-2"
                              title={member.is_admin ? 'Remove as group admin' : 'Make group admin'}
                              data-testid={`promote-member-${member.user_id}`}
                            >
                              <ShieldCheck size={20} weight={member.is_admin ? 'fill' : 'regular'} />
                            </button>
                            {!member.is_admin && (
                              <button
                                onClick={() => removeMember(String(member.user_id || ''))}
                                className="text-red-600 hover:text-red-700 p-2"
                              >
                                <UserMinus size={20} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Media Tab */}
            {activeTab === 'media' && (
              <div>
                {media.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No media shared yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {media.map((item, index) => {
                      // Handle different URL formats
                      const imageUrl = item.image_url || item.url;
                      if (!imageUrl) {
                        console.warn('No image URL for media item:', item);
                        return null;
                      }
                      
                      // Consistent URL construction
                      let fullUrl;
                      if (imageUrl.startsWith('http')) {
                        fullUrl = imageUrl; // Already complete URL
                      } else if (imageUrl.startsWith('/api/')) {
                        fullUrl = `${API}${imageUrl}`; // /api/media/... → https://.../api/media/...
                      } else if (imageUrl.startsWith('/uploads/')) {
                        fullUrl = `${API}/api${imageUrl}`; // /uploads/... → https://.../api/uploads/...
                      } else {
                        fullUrl = `${API}/api${imageUrl}`; // Fallback
                      }
                      
                      console.log(`Media ${index}:`, imageUrl, '→', fullUrl);
                      
                      return (
                        <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={fullUrl}
                            alt={`Media ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => handleImageClick(imageUrl)}
                            onLoad={() => console.log('✅ Loaded:', fullUrl)}
                            onError={(e) => {
                              console.error('❌ Failed:', fullUrl, 'Original:', imageUrl);
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="14"%3ELoad Failed%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-4">
                <button
                  onClick={leaveGroup}
                  className="w-full bg-yellow-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-yellow-700"
                >
                  <SignOut size={20} weight="bold" />
                  Leave Group
                </button>

                {/* Latest-fix Point 9 — system MB withdraw groups
                    (system_key starts with "withdraw_group") can NEVER be
                    deleted, even by the creator. Only regular groups. */}
                {isCreator && !String(group?.system_key || '').startsWith('withdraw_group') && (
                  <button
                    onClick={deleteGroup}
                    className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-red-700"
                  >
                    <Trash size={20} weight="bold" />
                    Delete Group
                  </button>
                )}

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2 text-gray-700">Group Details</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {new Date(group?.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                    </div>
                    <div>
                      <span className="font-medium">Group ID:</span>{' '}
                      {groupId}
                    </div>
                    <div>
                      <span className="font-medium">Total Members:</span>{' '}
                      {group?.members?.length || 0}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>

    {/* Image Preview Modal with Back Button */}
    {showImagePreview && previewImageUrl && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
        onClick={closeImagePreview}
      >
        {/* Back Button - Top Left */}
        <button 
          onClick={closeImagePreview}
          className="absolute top-4 left-4 text-white hover:bg-white hover:bg-opacity-20 p-3 rounded-full transition-all z-60"
        >
          <ArrowLeft size={28} weight="bold" />
        </button>
        
        {/* Close Button - Top Right */}
        <button 
          onClick={closeImagePreview}
          className="absolute top-4 right-4 text-white text-4xl font-bold hover:bg-white hover:bg-opacity-20 w-12 h-12 flex items-center justify-center rounded-full transition-all z-60"
        >
          ×
        </button>
        
        {/* Image */}
        <img 
          src={previewImageUrl} 
          alt="Preview" 
          className="max-w-full max-h-full object-contain cursor-zoom-out"
          onClick={(e) => {
            e.stopPropagation();
            closeImagePreview();
          }}
        />
      </div>
    )}
    </>
  );
};

export default GroupDashboard;
